import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Check, ListTodo, Loader2, MoreHorizontal, Pencil, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import DeleteWithReasonDialog from "../components/DeleteWithReasonDialog";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import {
  IMPACT_LABELS, LEVEL_LABELS, LEVEL_TONES, PROBABILITY_LABELS, RISK_ACTION_HINTS, RISK_ACTION_LABELS, RISK_HISTORY_LABELS, RISK_REASON_ACTIONS, RISK_STATUS_LABELS,
  formatDay, governanceError,
} from "../lib/governance";
import { TASK_STATUS_LABELS } from "../lib/tasks";
import { formatDateTime } from "../lib/tickets";
import useDirectory from "../hooks/useDirectory";
import { Chip, Field, LinkList } from "../components/governance/GovernanceBits";
import TaskFormSheet from "../components/TaskFormSheet";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem,
  ResponsiveDropdownMenuTrigger as DropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

function Block({ title, children, testid }) {
  return (
    <Card className="p-4 sm:p-5 space-y-3" data-testid={testid}>
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      {children}
    </Card>
  );
}

const ScaleField = ({ label, labels, value, onChange, testid }) => (
  <div className="space-y-1.5"><Label>{label}</Label>
    <Select value={String(value)} onValueChange={(v) => onChange(Number(v))}>
      <SelectTrigger className="h-10" aria-label={label} data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>{Object.entries(labels).map(([n, text]) => <SelectItem key={n} value={n}>{`${n} — ${text}`}</SelectItem>)}</SelectContent>
    </Select></div>
);

// Review, accept, close, reopen: one dialog, the fields each gesture asks for.
function ActionDialog({ action, risk, onClose, onDone }) {
  const [impact, setImpact] = useState(risk.impact);
  const [probability, setProbability] = useState(risk.probability);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const review = action === "review";
  const needsReason = RISK_REASON_ACTIONS.includes(action);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = review ? { expected_version: risk.version, impact, probability, note: text.trim() || null } : { expected_version: risk.version, reason: text.trim() };
      const { data } = await api.post(`/governance/risks/${risk.id}/${action}`, body);
      toast.success(review ? "Risque revu" : action === "accept" ? "Risque accepté" : action === "close" ? "Risque clos" : "Risque rouvert");
      onDone(data);
    } catch (e) {
      setError(governanceError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={`risk-dialog-${action}`}>
        <DialogHeader><DialogTitle>{RISK_ACTION_LABELS[action]} — {risk.number}</DialogTitle><DialogDescription>{RISK_ACTION_HINTS[action]}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          {review && (
            <div className="grid gap-3 sm:grid-cols-2">
              <ScaleField label="Impact" labels={IMPACT_LABELS} value={impact} onChange={setImpact} testid="review-impact" />
              <ScaleField label="Probabilité" labels={PROBABILITY_LABELS} value={probability} onChange={setProbability} testid="review-probability" />
            </div>
          )}
          <div className="space-y-1.5"><Label htmlFor="rk-text">{needsReason ? "Motif" : "Note (facultatif)"}</Label>
            <Textarea id="rk-text" rows={3} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} data-testid="risk-action-text" /></div>
          {error && <p className="text-sm text-red-600" role="alert" data-testid="risk-action-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant={action === "close" ? "destructive" : "default"} onClick={submit} disabled={busy || (needsReason && text.trim().length < 3)} data-testid="risk-action-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{RISK_ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function GovernanceRisk() {
  const { riskId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [risk, setRisk] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [dialog, setDialog] = useState(null);
  const [taskFor, setTaskFor] = useState(null); // the measure a task is being created for
  const canTask = hasPermission(user, "tasks:create");
  const directory = useDirectory(canTask && hasPermission(user, "tasks:scope_all"));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/governance/risks/${riskId}`);
      setRisk(data);
      setPhase("ready");
    } catch (e) {
      setPhase(e?.response?.status === 404 ? "missing" : "error");
    }
  }, [riskId]);
  useEffect(() => { setPhase("loading"); load(); }, [load]);

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-72" /><Skeleton className="h-40 w-full" /></div>;
  if (phase === "missing") {
    return (
      <Card className="p-8 text-center space-y-2" data-testid="risk-missing">
        <h2 className="font-medium text-slate-900">Risque introuvable</h2>
        <p className="text-sm text-slate-500">Il n'existe pas, ou vous n'y avez pas accès.</p>
        <Button asChild variant="outline"><Link to="/gouvernance/risques">Retour aux risques</Link></Button>
      </Card>
    );
  }
  if (phase === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="risk-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">Impossible de charger ce risque.</p>
        <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  const cap = risk.capabilities;
  const others = cap.actions.filter((a) => a !== "review");
  const canDelete = risk.status === "open" && hasPermission(user, "governance:delete");
  const done = (data) => { setDialog(null); setRisk(data); };
  const patch = async (body, failure) => {
    try {
      const { data } = await api.patch(`/governance/risks/${risk.id}`, { expected_version: risk.version, ...body });
      setRisk(data);
    } catch (e) {
      toast.error(governanceError(e, failure));
    }
  };
  const setMeasureStatus = (measure, status) => patch({ measures: risk.measures.map((m) => ({ id: m.id, text: m.text, status: m.id === measure.id ? status : m.status })) }, "Impossible de modifier la mesure.");
  const linkTask = async (task) => {
    const measure = taskFor;
    setTaskFor(null);
    try {
      const { data } = await api.post(`/governance/risks/${risk.id}/measures/${measure.id}/task`, { task_id: task.id });
      setRisk(data);
      toast.success("Tâche reliée à la mesure");
    } catch (e) {
      toast.error(governanceError(e, "La tâche est créée mais n'a pas pu être reliée."));
    }
  };
  const unlinkTask = async (measure) => {
    try {
      const { data } = await api.delete(`/governance/risks/${risk.id}/measures/${measure.id}/task`);
      setRisk(data);
    } catch (e) {
      toast.error(governanceError(e, "Impossible de retirer la tâche."));
    }
  };

  return (
    <div className="space-y-4" data-testid="risk-page">
      <div>
        <Link to="/gouvernance/risques" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Risques</Link>
        <div className="mt-1 flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-slate-500 tabular-nums" data-testid="risk-number">{risk.number} · {risk.category_label}</p>
            <h1 className="font-display font-bold text-2xl text-slate-900 break-words" data-testid="risk-title">{risk.title}</h1>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              <Chip label={LEVEL_LABELS[risk.level]} tone={LEVEL_TONES[risk.level]} testid="risk-level" />
              {risk.status !== "open" && <Chip label={RISK_STATUS_LABELS[risk.status]} tone="slate" testid="risk-status" />}
              {risk.to_review && <Chip label="À revoir" tone="amber" testid="risk-to-review" />}
              {risk.restricted && <Chip label="Restreint" tone="slate" testid="risk-restricted" />}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cap.review && <Button onClick={() => setDialog("review")} data-testid="risk-review"><Check size={15} className="mr-1.5" />{RISK_ACTION_LABELS.review}</Button>}
            {cap.edit && <Button asChild variant="outline" data-testid="risk-edit"><Link to={`/gouvernance/risques/${risk.id}/modifier`}><Pencil size={14} className="mr-1.5" />Modifier</Link></Button>}
            {(others.length > 0 || canDelete) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="icon" aria-label="Autres actions" data-testid="risk-more"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {others.map((a) => <DropdownMenuItem key={a} onClick={() => setDialog(a)} className={a === "close" ? "text-red-600" : undefined} data-testid={`risk-${a}`}>{RISK_ACTION_LABELS[a]}</DropdownMenuItem>)}
                  {canDelete && <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="risk-delete">Supprimer le risque…</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] items-start">
        <div className="space-y-4 min-w-0">
          {risk.description && <Block title="Description"><p className="text-sm text-slate-800 whitespace-pre-wrap" data-testid="risk-description">{risk.description}</p></Block>}
          <Block title="Évaluation" testid="risk-assessment">
            <div className="grid gap-3 sm:grid-cols-3 text-sm">
              <div><div className="text-xs text-slate-500">Impact</div><div className="font-medium text-slate-900">{risk.impact} — {IMPACT_LABELS[risk.impact]}</div></div>
              <div><div className="text-xs text-slate-500">Probabilité</div><div className="font-medium text-slate-900">{risk.probability} — {PROBABILITY_LABELS[risk.probability]}</div></div>
              <div><div className="text-xs text-slate-500">Niveau</div><div className="font-medium text-slate-900">{LEVEL_LABELS[risk.level]} <span className="text-slate-500 font-normal">(score {risk.score})</span></div></div>
            </div>
          </Block>
          <Block title="Mesures" testid="risk-measures">
            {risk.measures.length === 0 ? <p className="text-sm text-slate-500" data-testid="measures-empty">Aucune mesure pour le moment.</p> : (
              <ul className="divide-y divide-slate-100">
                {risk.measures.map((m) => (
                  <li key={m.id} className="py-2 space-y-1" data-testid={`measure-${m.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <label className="flex items-start gap-2 text-sm cursor-pointer min-w-0">
                        <input type="checkbox" className="mt-1 shrink-0" checked={m.status === "done"} disabled={!cap.edit} onChange={(e) => setMeasureStatus(m, e.target.checked ? "done" : "todo")} aria-label={`Mesure faite : ${m.text}`} data-testid={`measure-done-${m.id}`} />
                        <span className={m.status === "done" ? "line-through text-slate-500" : "text-slate-900"}>{m.text}</span>
                      </label>
                      {cap.link_tasks && canTask && !m.task && m.status !== "done" && <Button size="sm" variant="outline" className="shrink-0" onClick={() => setTaskFor(m)} data-testid={`measure-task-new-${m.id}`}>Créer une tâche</Button>}
                    </div>
                    {m.task && (
                      <div className="ml-6 flex items-center justify-between gap-2" data-testid={`measure-task-${m.id}`}>
                        {m.task.visible ? (
                          <Link to="/tasks" state={{ openTask: m.task.id }} className="min-w-0 flex items-center gap-2 text-sm text-slate-700 hover:text-jamora">
                            <ListTodo size={14} className="text-slate-400 shrink-0" /><span className="truncate">{m.task.title}</span>
                            <span className="text-xs text-slate-500 shrink-0">{TASK_STATUS_LABELS[m.task.status] || m.task.status}{m.task.due_date ? ` · ${formatDay(m.task.due_date)}` : ""}</span>
                          </Link>
                        ) : <span className="text-sm italic text-slate-500">Tâche non visible avec vos droits</span>}
                        {cap.link_tasks && <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" onClick={() => unlinkTask(m)} aria-label="Retirer la tâche de la mesure"><X size={14} /></Button>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Block>
          {risk.decisions.length > 0 && (
            <Block title="Décisions liées" testid="risk-decisions">
              <ul className="divide-y divide-slate-100">
                {risk.decisions.map((d) => (
                  <li key={d.id} className="py-2 text-sm">
                    {d.visible ? <Link to={`/gouvernance/decisions/${d.id}`} className="text-slate-900 hover:text-jamora"><span className="text-xs text-slate-500 mr-2 tabular-nums">{d.number}</span>{d.title}</Link>
                      : <span className="italic text-slate-500">Décision non visible avec vos droits</span>}
                  </li>
                ))}
              </ul>
            </Block>
          )}
          {risk.links.length > 0 && <Block title="Liens" testid="risk-links"><LinkList links={risk.links} onRemove={cap.edit ? (l) => patch({ links: risk.links.filter((x) => x !== l).map((x) => ({ kind: x.kind, ref_id: x.ref_id })) }, "Impossible de retirer ce lien.") : undefined} /></Block>}
          <Block title="Historique" testid="risk-history">
            <ol className="space-y-2">
              {risk.history.map((h) => (
                <li key={h.id} className="border-l-2 border-slate-200 pl-3">
                  <div className="text-sm text-slate-900">{RISK_HISTORY_LABELS[h.kind] || h.kind}</div>
                  <div className="text-xs text-slate-500">{formatDateTime(h.at)}{h.actor_name ? ` · ${h.actor_name}` : ""}</div>
                  {h.assessment && <div className="text-xs text-slate-600" data-testid="history-assessment">Impact {h.assessment.from.impact} → {h.assessment.to.impact} · probabilité {h.assessment.from.probability} → {h.assessment.to.probability}</div>}
                  {h.note && <div className="text-xs text-slate-600">Note : {h.note}</div>}
                  {h.reason && <div className="text-xs text-slate-600">Motif : {h.reason}</div>}
                </li>
              ))}
            </ol>
          </Block>
        </div>
        <Card className="p-4 lg:sticky lg:top-4" data-testid="risk-side">
          <h2 className="text-base font-semibold text-slate-900 mb-1">Fiche</h2>
          <dl className="divide-y divide-slate-100">
            <Field label="Propriétaire">{risk.owner.name || "—"}</Field>
            <Field label="Catégorie">{risk.category_label}</Field>
            <Field label="Dernière revue">{formatDay(risk.last_review_on)}</Field>
            {risk.status === "open" && <Field label="Prochaine revue"><span data-testid="risk-next-review">{formatDay(risk.next_review_on)}</span></Field>}
            <Field label="Périodicité">{risk.review_months === 1 ? "Tous les mois" : `Tous les ${risk.review_months} mois`}</Field>
            <Field label="Consigné par">{risk.author.name || "—"}</Field>
          </dl>
        </Card>
      </div>
      {dialog && dialog !== "delete" && <ActionDialog action={dialog} risk={risk} onClose={() => setDialog(null)} onDone={done} />}
      <DeleteWithReasonDialog
        target={dialog === "delete" ? risk : null} testId="delete-governance" onClose={() => setDialog(null)}
        title={`Supprimer le risque ${risk.number} ?`}
        description="Il va dans la corbeille et se restaure pendant 30 jours. Un risque déjà revu, avec des décisions ou des actions, s'accepte ou se clôt à la place."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/governance/risks/${risk.id}`, { data: { reason } });
            toast.success("Mis à la corbeille");
            navigate("/gouvernance/risques");
          } catch (e) {
            toast.error(governanceError(e, "Impossible de supprimer cet élément."));
            setDialog(null);
          }
        }}
      />
      {canTask && taskFor && (
        <TaskFormSheet open onOpenChange={(o) => { if (!o) setTaskFor(null); }} task={null} canScopeAll={hasPermission(user, "tasks:scope_all")} canEdit={false} directory={directory}
          initialValues={{ title: taskFor.text.slice(0, 120), domain: "internal_organization", description: `Mesure du risque ${risk.number}.` }} onSaved={linkTask} />
      )}
    </div>
  );
}
